import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, AlertTriangle, X, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getRiskColor = (level) => {
  switch (level) {
    case 'critical': return 'border-rose-300 bg-rose-50';
    case 'high': return 'border-rose-200 bg-rose-50';
    default: return 'border-amber-200 bg-amber-50';
  }
};

const getRiskBadge = (level) => {
  switch (level) {
    case 'critical': return 'bg-rose-600 text-white';
    case 'high': return 'bg-rose-500 text-white';
    default: return 'bg-amber-500 text-white';
  }
};

export default function AlertNotifications() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const panelRef = useRef(null);

  const connectWs = useCallback(() => {
    // Auth rides on the httpOnly access_token cookie sent with the handshake;
    // a token must never be placed in the URL (it ends up in access logs).
    const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws/alerts';

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsStatus('connected');
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'high_risk_alert' || data.type === 'adverse_media_alert') {
            const isMedia = data.type === 'adverse_media_alert';
            const alert = {
              ...data,
              id: data.job_id || data.scan_id || Date.now().toString(),
              timestamp: data.at || data.scan_date || new Date().toISOString(),
              query_name: data.query_name || data.subject_name,
              match_count: isMedia ? (data.findings_count || 0) : data.match_count,
              domain: isMedia ? 'adverse_media' : 'sanctions',
              read: false,
            };
            setAlerts((prev) => [alert, ...prev].slice(0, 50));
            setUnreadCount((c) => c + 1);
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setWsStatus('disconnected');
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connectWs, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      // Will retry via reconnect
    }
  }, []);

  useEffect(() => {
    connectWs();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connectWs]);

  // Keep-alive ping every 25s
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);
    return () => clearInterval(interval);
  }, []);

  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const togglePanel = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setUnreadCount(0);
  };

  const dismissAlert = (alertId) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
  };

  const viewScan = (scanId) => {
    setIsOpen(false);
    navigate('/results', { state: { scanId } });
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef} data-testid="alert-notifications">
      {/* Bell Button */}
      <button
        onClick={togglePanel}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="alert-bell-button"
      >
        <Bell className={`w-5 h-5 ${wsStatus === 'connected' ? 'text-slate-700' : 'text-slate-400'}`} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse"
            data-testid="alert-unread-count"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute right-0 top-12 w-96 max-h-[480px] bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="alert-panel"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500" />
              <h3 className="text-sm font-semibold text-slate-900">Risk Alerts</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${wsStatus === 'connected' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              <span className="text-[10px] text-slate-500">{wsStatus === 'connected' ? 'Live' : 'Offline'}</span>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[400px] divide-y divide-slate-100">
            {alerts.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No alerts yet</p>
                <p className="text-xs text-slate-400 mt-1">High-risk scan results will appear here in real time</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`px-4 py-3 border-l-4 ${getRiskColor(alert.risk_level)} hover:bg-slate-50/80 transition-colors`}
                  data-testid={`alert-item-${alert.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[10px] ${getRiskBadge(alert.risk_level)}`}>
                          {alert.risk_level?.toUpperCase()}
                        </Badge>
                        <span className="text-[10px] text-slate-400">{timeAgo(alert.timestamp)}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900 truncate">{alert.query_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {alert.domain === 'adverse_media' ? (
                          alert.kind === 'failed'
                            ? 'Adverse-media discovery failed — not a clean result'
                            : `Adverse media: ${alert.match_count} finding${alert.match_count !== 1 ? 's' : ''}, unvalidated`
                        ) : (
                          <>
                            {alert.match_count} match{alert.match_count !== 1 ? 'es' : ''}
                            {alert.top_sources?.length > 0 && ` from ${alert.top_sources.join(', ')}`}
                          </>
                        )}
                      </p>
                      {alert.scanned_by && (
                        <p className="text-[10px] text-slate-400 mt-0.5">Scanned by {alert.scanned_by}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-slate-400 hover:text-blue-600"
                        onClick={() => viewScan(alert.scan_id)}
                        data-testid={`alert-view-${alert.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-slate-400 hover:text-rose-600"
                        onClick={() => dismissAlert(alert.id)}
                        data-testid={`alert-dismiss-${alert.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {alerts.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => { setAlerts([]); setUnreadCount(0); }}
                className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="clear-all-alerts"
              >
                Clear all alerts
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
